// public/js/features/assets/asset-charts.js
// Sensor-history charts section for the asset detail page. Reuses the shared
// chart grid renderer; lays the small charts out multi-column on desktop.

import { RANGES, fetchHistoryPoints, fetchHistoryRange, renderSensorChartGrid, uplinkIntervalLabel, getLastCoverage } from '../../modules/charts/sensor-charts.js';
import { deviceLabelMap } from './slot-utils.js';
import { renderBatteryRemainingCards } from '../tracker-config/battery-remaining-chart.js';
import { canExport } from '../../modules/rbac.js';
import { openRangePickerModal } from '../../modules/ui/range-picker-modal.js';

// Custom date-range history is a Data Export add-on feature. The button is always
// shown (so everyone can discover it), but only the entitled can open the picker;
// others get an info hint. Capped so a single query can't pull an unbounded slice.
const MAX_CUSTOM_DAYS = 120;

let charts = [];

function destroy() {
  charts.forEach(c => { try { c.destroy(); } catch (e) {} });
  charts = [];
}

/**
 * @param {HTMLElement} sectionEl  container for the chart grid
 * @param {HTMLElement} rangesEl   container for the 24h/7d/14d toggle buttons
 * @param {string} assetUUID
 * @param {HTMLElement} [reloadBtn] optional reload button
 */
export async function renderAssetChartsSection(sectionEl, rangesEl, assetUUID, reloadBtn, asset = null) {
  let rangeId = '14d';
  let points = [];
  const deviceLabels = asset ? deviceLabelMap(asset) : null;

  const exportOk = canExport();

  const paintRangeButtons = () => {
    rangesEl.querySelectorAll('.js-detail-range').forEach(b => {
      const active = b.dataset.range === rangeId;
      b.classList.toggle('btn-primary', active);
      b.classList.toggle('btn-outline-secondary', !active);
    });
    const cb = rangesEl.querySelector('.js-detail-range-custom');
    if (cb) {
      const active = rangeId === 'custom';
      cb.classList.toggle('btn-primary', active);
      cb.classList.toggle('btn-outline-secondary', !active);
    }
  };

  // A raw history read is capped server-side (config.telemetry.maxHistoryPoints)
  // and keeps the RECENT end of the window. Say so when it bites: drawing a
  // shortened window as though it were the one that was asked for is precisely
  // the bug that made a 40-day range look like 20 days.
  const coverageNote = () => {
    const c = getLastCoverage();
    if (!c || !c.truncated || !c.from) return '';
    const day = (iso) => (iso
      ? new Date(iso).toLocaleDateString(navigator.language, { day: '2-digit', month: '2-digit', year: '2-digit' })
      : '?');
    const asked = c.requestedSince ? ` You asked from ${day(c.requestedSince)}.` : '';
    return `<div class="alert alert-warning py-2 px-3 small mb-2 d-flex align-items-start gap-2">
        <i class="fa-solid fa-triangle-exclamation mt-1"></i>
        <span>This asset reports too densely to load the whole window at once.
        Showing the most recent ${c.maxPoints.toLocaleString(navigator.language)} readings,
        starting ${day(c.from)}.${asked} Pick a shorter window to see all of it.</span>
      </div>`;
  };

  const draw = () => {
    destroy();
    charts = renderSensorChartGrid(sectionEl, points, rangeId, deviceLabels);
    // renderSensorChartGrid owns sectionEl's innerHTML, so the note goes in after.
    const note = coverageNote();
    if (note) sectionEl.insertAdjacentHTML('afterbegin', note);
    // Battery-remaining projection per paired tracker, appended into the same
    // Device health block. Independent of the selected history range - it draws
    // the whole accounting window and projects to end of life.
    charts.push(...renderBatteryRemainingCards(sectionEl, asset?.assignedDevices || [], deviceLabels));
    const intervalEl = document.getElementById('detail-uplink-interval');
    if (intervalEl) {
      const lbl = uplinkIntervalLabel(points, rangeId);
      intervalEl.innerHTML = lbl
        ? `<i class="fa-solid fa-tower-broadcast me-1"></i>~${lbl} between uplinks`
        : '';
    }
  };

  const load = async () => {
    sectionEl.innerHTML = `<div class="text-muted small p-2"><i class="fa-solid fa-spinner fa-spin me-1"></i> Loading sensor history…</div>`;
    const icon = reloadBtn?.querySelector('i');
    if (reloadBtn) reloadBtn.disabled = true;
    icon?.classList.add('fa-spin');
    try {
      points = await fetchHistoryPoints(assetUUID);
    } catch (e) {
      console.warn('[asset-charts] history load failed', e);
      points = [];
    } finally {
      if (reloadBtn) reloadBtn.disabled = false;
      icon?.classList.remove('fa-spin');
    }
    draw();
  };

  rangesEl.innerHTML = RANGES.map(r =>
    `<button type="button" class="btn btn-sm ${r.id === rangeId ? 'btn-primary' : 'btn-outline-secondary'} js-detail-range" data-range="${r.id}">${r.label}</button>`
  ).join('') +
    `<button type="button" class="btn btn-sm btn-outline-secondary js-detail-range-custom" title="Custom date range${exportOk ? '' : ' — Data Export add-on'}">
       <i class="fa-regular fa-calendar-days me-1"></i>Custom${exportOk ? '' : ' <i class="fa-solid fa-lock ms-1"></i>'}
     </button>`;

  // Load an absolute custom window (from the shared picker modal) and draw it.
  const loadCustomWindow = async (fromMs, toMs) => {
    sectionEl.innerHTML = `<div class="text-muted small p-2"><i class="fa-solid fa-spinner fa-spin me-1"></i> Loading…</div>`;
    try {
      points = await fetchHistoryRange(assetUUID, new Date(fromMs).toISOString(), new Date(toMs).toISOString());
    } catch (e) {
      console.warn('[asset-charts] custom range load failed', e); points = [];
    }
    rangeId = 'custom';
    paintRangeButtons();
    draw();
  };

  rangesEl.querySelectorAll('.js-detail-range').forEach(b => {
    b.addEventListener('click', async () => {
      const wasCustom = rangeId === 'custom';
      rangeId = b.dataset.range;
      paintRangeButtons();
      // Leaving a custom window: `points` holds that absolute slice, so reload the
      // default rolling window before the preset filters it.
      if (wasCustom) await load(); else draw();
    });
  });

  rangesEl.querySelector('.js-detail-range-custom')?.addEventListener('click', () => {
    if (!exportOk) {
      window.showToast?.('Custom date ranges are part of the Data Export add-on.', 'info');
      return;
    }
    openRangePickerModal({
      title: 'Custom sensor history',
      hint: `Loads that window from the archive. Max ${MAX_CUSTOM_DAYS} days.`,
      maxDays: MAX_CUSTOM_DAYS,
      onApply: ({ fromMs, toMs }) => loadCustomWindow(fromMs, toMs),
    });
  });

  if (reloadBtn) reloadBtn.addEventListener('click', () => load());

  // Fullscreen is now per-chart: each card has its own expand button that opens an
  // in-app modal (see renderSensorChartGrid). No section-level browser fullscreen.

  await load();
}
